import { get } from "lodash-es";
import { MESApiService, getMomentTz, spParameter } from "@dexteel/mesf-core";
import { Entry } from "../models/Entry";
import {
	AttachmentUploadRequest,
	EntryAttachment,
} from "../models/EntryAttachment";

const moment = getMomentTz();

// The API returns UTC timestamps without a zone
export const toEntry = (entry: Entry): Entry => ({
	...entry,
	CreateTimestamp: entry.CreateTimestamp
		? moment.utc(entry["CreateTimestamp"]).toDate()
		: null,
	UpdateTimestamp: entry.UpdateTimestamp
		? moment.utc(entry["UpdateTimestamp"]).toDate()
		: null,
});

// A single entry with the same columns as the list, whatever the filters are
export const getEntry = async (entryId: number) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[LB].[GetEntries]", [
		{ name: "EntryId", value: entryId },
	]);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching entry");
	}
	const row = get(resp, "data.tables[0].rows[0]", null) as Entry | null;
	return row ? toEntry(row) : null;
};

// One shift (ShiftId), or every entry when no shift is given
export const getEntries = async ({
	shiftId,
	assetId,
	signal,
}: {
	shiftId?: number | null;
	assetId?: number | null;
	signal: AbortSignal | undefined;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	if (shiftId != null) {
		parameters.push({ name: "ShiftId", value: shiftId });
	}

	if (assetId != null) {
		parameters.push({ name: "AssetId", value: assetId as number });
	}

	const resp = await apiService.callV2("[LB].[GetEntries]", parameters, signal);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as Entry[];
		return rows.map(toEntry);
	} else {
		throw new Error(resp.message || "Error fetching entries");
	}
};

export const upsertEntry = async ({
	assetId,
	entryId,
	shiftId,
	title,
	content,
	createTimestamp,
	author,
}: {
	assetId: number | null;
	entryId: number | null;
	shiftId: number;
	title: string;
	content: string;
	createTimestamp: Date;
	author: string;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "EntryId", value: entryId },
		{ name: "ShiftId", value: shiftId },
		{ name: "Title", value: title },
		{ name: "Content", value: content },
		{
			name: "CreateTimestamp",
			value: createTimestamp.toISOString(),
		},
		{
			name: "UpdateTimestamp",
			value: new Date().toISOString(),
		},
		{ name: "Author", value: author },
	];

	if (assetId || assetId === 0) {
		parameters.push({ name: "AssetId", value: assetId });
	}

	const resp = await apiService.callV2("[LB].[UpsertEntry]", parameters);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0]", null) as Entry;
	}
	{
		throw new Error(resp.message || "Error upserting entry");
	}
};

export const deleteEntry = async (entryId: number | null) => {
	const apiService = new MESApiService();

	// First, get all attachments for this entry to identify files to delete
	let attachmentsToDelete: EntryAttachment[] = [];
	try {
		if (entryId) {
			attachmentsToDelete = await getEntryAttachments({ entryId });
		}
	} catch (error) {
		// If we can't get attachments, log but continue with entry deletion
		console.warn("Failed to get attachments for entry deletion:", error);
	}

	// Delete the entry (this will cascade delete attachment records)
	const parameters: spParameter[] = [{ name: "EntryId", value: entryId }];
	const resp = await apiService.callV2("[LB].[DeleteEntry]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting entry");
	}

	// Delete associated files in background (don't block the UI)
	if (attachmentsToDelete.length > 0) {
		deleteAttachedFilesInBackground(attachmentsToDelete);
	}
};

const deleteAttachedFilesInBackground = async (
	attachments: EntryAttachment[],
) => {
	// Delete files in background without blocking the UI
	Promise.all(
		attachments.map(async (attachment) => {
			try {
				await deleteUploadFile(attachment.UploadFileId);
			} catch (error) {
				// Log errors but don't fail the entire operation
				console.error(
					`Failed to delete file ${attachment.UploadFileId}:`,
					error,
				);
			}
		}),
	).catch((error) => {
		console.error("Background file deletion failed:", error);
	});
};

export const deleteUploadFile = async (uploadFileId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "UploadFileId", value: uploadFileId },
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[DeleteUploadFile]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting upload file");
	}
};

// Attachment management functions
export const getEntryAttachments = async (
	params: { entryId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "EntryId", value: params.entryId },
	];

	const resp = await apiService.callV2(
		"[LB].[GetEntryAttachments]",
		parameters,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []) as EntryAttachment[];

		rows = rows.map((attachment: EntryAttachment) => ({
			...attachment,
			CreationTime: attachment.CreationTime
				? moment.utc(attachment.CreationTime).toDate()
				: new Date(),
		}));

		return rows;
	} else {
		throw new Error(resp.message || "Error fetching entry attachments");
	}
};

export const upsertEntryAttachment = async ({
	entryId,
	uploadFileId,
	attachmentName,
	attachmentURL,
}: AttachmentUploadRequest) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "EntryId", value: entryId },
		{ name: "UploadFileId", value: uploadFileId },
		{ name: "AttachmentName", value: attachmentName },
		{ name: "AttachmentURL", value: attachmentURL },
	];

	const resp = await apiService.callV2(
		"[LB].[UpsertEntryAttachment]",
		parameters,
	);

	if (resp.ok) {
		const attachment = get(
			resp,
			"data.tables[0].rows[0]",
			null,
		) as EntryAttachment;
		if (attachment) {
			return {
				...attachment,
				CreationTime: attachment.CreationTime
					? moment.utc(attachment.CreationTime).toDate()
					: new Date(),
			};
		}
		return null;
	} else {
		throw new Error(resp.message || "Error creating entry attachment");
	}
};

export const deleteEntryAttachment = async (
	entryAttachmentId: number,
	uploadFileId?: number,
) => {
	const apiService = new MESApiService();

	// Delete the attachment record
	const parameters: spParameter[] = [
		{ name: "EntryAttachmentId", value: entryAttachmentId },
	];

	const resp = await apiService.callV2(
		"[LB].[DeleteEntryAttachment]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting entry attachment");
	}

	// Delete the associated file in background if UploadFileId was provided
	if (uploadFileId) {
		deleteUploadFileInBackground(uploadFileId);
	}
};

const deleteUploadFileInBackground = async (uploadFileId: number) => {
	// Delete file in background without blocking the UI
	try {
		await deleteUploadFile(uploadFileId);
	} catch (error) {
		// Log errors but don't fail the operation
		console.error(`Failed to delete file ${uploadFileId}:`, error);
	}
};
